import { useEffect, useState } from 'react';
import { Loader2, MoreVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePickerButton } from '@/components/ui/date-picker-button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type GiftCardSkuRow = {
  sku: string;
  product_id: number | null;
  main_title: string | null;
  start_date: string | null;
  end_date: string | null;
};

type GiftCardSkuResponse = {
  count: number;
  results: GiftCardSkuRow[];
};

type EarnDebitPresetRow = {
  id: number;
  gift_card_sku: string;
  start_date: string;
  end_date: string;
  earn_debit: number;
  updated_at: string | null;
};

type EarnDebitPresetListResponse = {
  count: number;
  results: EarnDebitPresetRow[];
};

const TZ8 = 'Asia/Singapore';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function getTz8YearMonthNow(): { year: number; monthIndex: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ8,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, monthIndex: month - 1 };
}

function getLastThreeMonthsRangeTz8(): { startDate: string; endDate: string } {
  const { year, monthIndex } = getTz8YearMonthNow();
  const startAnchor = new Date(Date.UTC(year, monthIndex - 2, 1));
  const endAnchor = new Date(Date.UTC(year, monthIndex + 1, 0));
  const startDate = `${startAnchor.getUTCFullYear()}-${pad2(startAnchor.getUTCMonth() + 1)}-01`;
  const endDate = `${endAnchor.getUTCFullYear()}-${pad2(endAnchor.getUTCMonth() + 1)}-${pad2(endAnchor.getUTCDate())}`;
  return { startDate, endDate };
}

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function toYyyyMmDd(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
  }
  const yyyy = String(d.getFullYear()).padStart(4, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDateTime(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function fmtDate(value: string | null): string {
  if (!value) return '-';
  return toYyyyMmDd(value) || value;
}

function parseGiftSkuResponse(raw: unknown): GiftCardSkuResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(obj.results) ? obj.results : [];
  const parsedRows: GiftCardSkuRow[] = rows.map((item) => {
    const r = item as Record<string, unknown>;
    const productId = Number(r.product_id);
    return {
      sku: String(r.sku ?? ''),
      product_id: Number.isFinite(productId) ? productId : null,
      main_title: r.main_title == null ? null : String(r.main_title),
      start_date: r.start_date == null ? null : String(r.start_date),
      end_date: r.end_date == null ? null : String(r.end_date),
    };
  });
  return {
    count: toNum(obj.count),
    results: parsedRows.filter((r) => r.sku),
  };
}

function parsePresetResponse(raw: unknown): EarnDebitPresetListResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(obj.results) ? obj.results : [];
  const parsedRows: EarnDebitPresetRow[] = rows
    .map((item) => {
      const r = item as Record<string, unknown>;
      const id = Number(r.id);
      const earnDebit = Number(r.earn_debit ?? 0);
      const sku = String(r.gift_card_sku ?? '').trim();
      const startDate = String(r.start_date ?? '').trim();
      const endDate = String(r.end_date ?? '').trim();
      if (!Number.isFinite(id) || !sku || !startDate || !endDate) return null;
      return {
        id,
        gift_card_sku: sku,
        start_date: startDate,
        end_date: endDate,
        earn_debit: Number.isFinite(earnDebit) ? earnDebit : 0,
        updated_at: r.updated_at == null ? null : String(r.updated_at),
      };
    })
    .filter(Boolean) as EarnDebitPresetRow[];
  return {
    count: toNum(obj.count),
    results: parsedRows,
  };
}

export function EarnPointsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GiftCardSkuResponse>({ count: 0, results: [] });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<GiftCardSkuRow | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [earnDebit, setEarnDebit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [presetLoading, setPresetLoading] = useState(false);
  const [presetError, setPresetError] = useState<string | null>(null);
  const [presetSubmitMessage, setPresetSubmitMessage] = useState<string | null>(null);
  const [presets, setPresets] = useState<EarnDebitPresetRow[]>([]);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<EarnDebitPresetRow | null>(null);
  const [deletingPreset, setDeletingPreset] = useState<EarnDebitPresetRow | null>(null);
  const [presetSku, setPresetSku] = useState('');
  const [presetStartDate, setPresetStartDate] = useState('');
  const [presetEndDate, setPresetEndDate] = useState('');
  const [presetEarnDebit, setPresetEarnDebit] = useState('');
  const [presetSubmitting, setPresetSubmitting] = useState(false);
  const [presetDeleting, setPresetDeleting] = useState(false);

  async function loadRows() {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/earn-code-manager/earn-record/gift-card-zero-debit-skus/', {
        method: 'GET',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      setData(parseGiftSkuResponse(raw));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gift-card SKU rows.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRows();
    void loadPresetRows();
  }, []);

  async function loadPresetRows() {
    const range = getLastThreeMonthsRangeTz8();
    setPresetLoading(true);
    setPresetError(null);
    try {
      const qs = new URLSearchParams({
        start_date: range.startDate,
        end_date: range.endDate,
      });
      const resp = await fetch(`/v1/api/earn-code-manager/free-gift/earn-debit-presets/?${qs.toString()}`, {
        method: 'GET',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      setPresets(parsePresetResponse(raw).results);
    } catch (e) {
      setPresetError(e instanceof Error ? e.message : 'Failed to load earn debit presets.');
    } finally {
      setPresetLoading(false);
    }
  }

  function openAddEarnDebit(row: GiftCardSkuRow) {
    setSelectedRow(row);
    setStartDate(toYyyyMmDd(row.start_date));
    setEndDate(toYyyyMmDd(row.end_date));
    setEarnDebit('');
    setSubmitMessage(null);
    setDialogOpen(true);
  }

  async function onSubmitEarnDebit() {
    if (!selectedRow || submitting) return;

    const earnDebitNum = Number(earnDebit);
    if (!startDate || !endDate) {
      setError('Start date and end date are required.');
      return;
    }
    if (!Number.isFinite(earnDebitNum) || earnDebitNum < 0) {
      setError('Earn debit must be a valid number >= 0.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSubmitMessage(null);
    try {
      const upsertResp = await fetch('/v1/api/earn-code-manager/free-gift/earn-debit-range-upsert/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          sku: selectedRow.sku,
          earn_debit: earnDebitNum,
        }),
      });
      if (!upsertResp.ok) {
        const text = await upsertResp.text().catch(() => '');
        throw new Error(`Upsert failed (${upsertResp.status})${text ? `: ${text}` : ''}`);
      }

      const recomputeResp = await fetch('/v1/api/earn-code-manager/earn-record/recompute/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!recomputeResp.ok) {
        const text = await recomputeResp.text().catch(() => '');
        throw new Error(`Recompute failed (${recomputeResp.status})${text ? `: ${text}` : ''}`);
      }

      await loadRows();
      setSubmitMessage('Earn debit updated successfully. Data has been recomputed and refreshed.');
      setDialogOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit earn debit.');
    } finally {
      setSubmitting(false);
    }
  }

  function openAddPresetDialog() {
    const range = getLastThreeMonthsRangeTz8();
    setEditingPreset(null);
    setPresetSku('');
    setPresetStartDate(range.startDate);
    setPresetEndDate(range.endDate);
    setPresetEarnDebit('');
    setPresetSubmitMessage(null);
    setPresetError(null);
    setPresetDialogOpen(true);
  }

  function openEditPresetDialog(row: EarnDebitPresetRow) {
    setEditingPreset(row);
    setPresetSku(row.gift_card_sku);
    setPresetStartDate(toYyyyMmDd(row.start_date));
    setPresetEndDate(toYyyyMmDd(row.end_date));
    setPresetEarnDebit(String(row.earn_debit));
    setPresetSubmitMessage(null);
    setPresetError(null);
    setPresetDialogOpen(true);
  }

  function openDeletePresetDialog(row: EarnDebitPresetRow) {
    setDeletingPreset(row);
    setDeleteDialogOpen(true);
  }

  async function onSubmitPreset() {
    if (presetSubmitting) return;

    const earnDebitNum = Number(presetEarnDebit);
    const sku = presetSku.trim();
    if (!sku) {
      setPresetError('Gift card SKU is required.');
      return;
    }
    if (!presetStartDate || !presetEndDate) {
      setPresetError('Start date and end date are required.');
      return;
    }
    if (!Number.isFinite(earnDebitNum) || earnDebitNum < 0) {
      setPresetError('Earn debit must be a valid number >= 0.');
      return;
    }

    setPresetSubmitting(true);
    setPresetError(null);
    setPresetSubmitMessage(null);
    try {
      const isEdit = Boolean(editingPreset);
      const resp = await fetch(
        isEdit
          ? `/v1/api/earn-code-manager/free-gift/earn-debit-presets/${editingPreset?.id}/`
          : '/v1/api/earn-code-manager/free-gift/earn-debit-presets/',
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            gift_card_sku: sku,
            start_date: presetStartDate,
            end_date: presetEndDate,
            earn_debit: earnDebitNum,
          }),
        }
      );
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`${isEdit ? 'Update' : 'Create'} failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      await loadPresetRows();
      setPresetSubmitMessage(isEdit ? 'Preset updated successfully.' : 'Preset created successfully.');
      setPresetDialogOpen(false);
    } catch (e) {
      setPresetError(e instanceof Error ? e.message : 'Failed to submit preset.');
    } finally {
      setPresetSubmitting(false);
    }
  }

  async function onDeletePreset() {
    if (!deletingPreset || presetDeleting) return;
    setPresetDeleting(true);
    setPresetError(null);
    setPresetSubmitMessage(null);
    try {
      const resp = await fetch(`/v1/api/earn-code-manager/free-gift/earn-debit-presets/${deletingPreset.id}/`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Delete failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      await loadPresetRows();
      setPresetSubmitMessage('Preset deleted successfully.');
      setDeleteDialogOpen(false);
      setDeletingPreset(null);
    } catch (e) {
      setPresetError(e instanceof Error ? e.message : 'Failed to delete preset.');
    } finally {
      setPresetDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Attention needed !</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Gift card without earn points are detected.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          ) : null}
          {submitMessage ? (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-300">
              {submitMessage}
            </div>
          ) : null}

          <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Product ID</TableHead>
                  <TableHead>Main Title</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading rows...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : data.results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.results.map((row) => (
                    <TableRow key={row.sku}>
                      <TableCell className="font-mono">{row.sku}</TableCell>
                      <TableCell>{row.product_id ?? '-'}</TableCell>
                      <TableCell>{row.main_title ?? '-'}</TableCell>
                      <TableCell>{fmtDateTime(row.start_date)}</TableCell>
                      <TableCell>{fmtDateTime(row.end_date)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => openAddEarnDebit(row)}>Add Earn Debit</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <CardHeader>
          <CardTitle className="text-base text-gray-900 dark:text-neutral-50">Earn Debit Presets</CardTitle>
          <CardDescription className="text-gray-600 dark:text-neutral-400">
            Presets overlapping the last three months from today.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {presetError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {presetError}
            </div>
          ) : null}
          {presetSubmitMessage ? (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-300">
              {presetSubmitMessage}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button onClick={openAddPresetDialog}>Add Preset Earn</Button>
          </div>

          <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gift Card SKU</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Earn Debit</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {presetLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading presets...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : presets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-gray-500 dark:text-neutral-400">
                      No presets found
                    </TableCell>
                  </TableRow>
                ) : (
                  presets.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono">{row.gift_card_sku}</TableCell>
                      <TableCell>{fmtDate(row.start_date)}</TableCell>
                      <TableCell>{fmtDate(row.end_date)}</TableCell>
                      <TableCell>{row.earn_debit}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => openEditPresetDialog(row)}>Edit / Update</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => openDeletePresetDialog(row)}>Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Earn Debit</DialogTitle>
            <DialogDescription>{selectedRow ? `SKU: ${selectedRow.sku}` : 'Configure earn debit range.'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">Start Date</p>
              <DatePickerButton value={startDate} onChange={setStartDate} placeholder="Select start date" />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">End Date</p>
              <DatePickerButton value={endDate} onChange={setEndDate} placeholder="Select end date" />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">Earn Debit</p>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={earnDebit}
                onChange={(e) => setEarnDebit(e.target.value)}
                placeholder="e.g. 12.50"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void onSubmitEarnDebit()} disabled={submitting || !selectedRow}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={presetDialogOpen} onOpenChange={setPresetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPreset ? 'Edit Preset Earn' : 'Add Preset Earn'}</DialogTitle>
            <DialogDescription>
              {editingPreset ? `Preset ID: ${editingPreset.id}` : 'Create a new earn debit preset row.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">Gift Card SKU</p>
              <Input value={presetSku} onChange={(e) => setPresetSku(e.target.value)} placeholder="e.g. GC5000" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">Start Date</p>
              <DatePickerButton value={presetStartDate} onChange={setPresetStartDate} placeholder="Select start date" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">End Date</p>
              <DatePickerButton value={presetEndDate} onChange={setPresetEndDate} placeholder="Select end date" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-neutral-50">Earn Debit</p>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={presetEarnDebit}
                onChange={(e) => setPresetEarnDebit(e.target.value)}
                placeholder="e.g. 12.50"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPresetDialogOpen(false)} disabled={presetSubmitting}>
                Cancel
              </Button>
              <Button onClick={() => void onSubmitPreset()} disabled={presetSubmitting}>
                {presetSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingPreset ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Preset</DialogTitle>
            <DialogDescription>
              {deletingPreset
                ? `Delete preset #${deletingPreset.id} (${deletingPreset.gift_card_sku})? This action cannot be undone.`
                : 'Delete this preset row?'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={presetDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void onDeletePreset()} disabled={presetDeleting}>
              {presetDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
